import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard, Newspaper, Users, UserPlus, UserMinus, Network, CalendarCheck, CalendarOff,
  CalendarClock, CalendarHeart, Timer, Banknote, BadgeIndianRupee, ReceiptIndianRupee, HandCoins,
  Landmark, ShieldCheck, FileCheck2, History, Target, Mail, FolderOpen, LifeBuoy, Megaphone,
  ChartColumn, ChartLine, FileUp, MessagesSquare, Briefcase, Laptop, BookOpen, CircleUser,
  ReceiptText, Building2, Palette, Layers, LayoutGrid, DatabaseBackup, Gauge, Building, CreditCard,
  KeyRound, UserCog, ChartPie, PanelsTopLeft, ListChecks, CalendarSync, Bell, IdCard, UserCheck, ScanFace,
  BookOpenCheck, UsersRound, ClipboardCheck, DoorOpen, ClipboardList,
  CalendarRange, GitCompareArrows, DatabaseZap, BriefcaseBusiness, Wallet,
  GitBranch, Sparkles, FileSpreadsheet, Contact, SlidersHorizontal,
} from 'lucide-react'

/**
 * Navigation register — the ONE place a destination gets its label and icon.
 * Rule: one concept, one icon. The old sidebar reused Wallet for 7 destinations and
 * FileText for 8; that made icons meaningless. It also labelled /announcements "Feed",
 * colliding with /feed. Both fixed here.
 *
 * `roles` mirrors the route guards in src/router/routes.tsx, so a link is only shown to a
 * role the page will actually admit (previously EMPLOYEE saw Settings, clicked, got 403).
 * SUPER_ADMIN passes every role check (useAuth.hasRole).
 */
export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  roles?: string[]
  /**
   * Exact roles (no SUPER_ADMIN pass-through) that see this entry. Used for self-service
   * shortcuts placed in My space for an EMPLOYEE, whose work-area twin is HR/manager-only.
   */
  audience?: string[]
  /** Extra words the command palette should match (synonyms users type). */
  keywords?: string
}

export interface NavSection {
  title: string
  items: NavItem[]
}

const HR = ['HR_ADMIN', 'HR_EXECUTIVE']
const ADMIN_ONLY = ['HR_ADMIN']
const PLATFORM = ['SUPER_ADMIN']
/** Roles the approval endpoints admit (leave/claims/regularisation/exit act routes). */
const APPROVERS = ['HR_ADMIN', 'HR_EXECUTIVE', 'MANAGER']
/** Roles with an employee record of their own (CA partners have none). */
const STAFF = ['HR_ADMIN', 'HR_EXECUTIVE', 'MANAGER', 'EMPLOYEE']
/**
 * Spec 21 §3.2.4: an EMPLOYEE has no People/Time area (they use My space) and only a read +
 * helpdesk slice of Workplace; a CA_PARTNER has no Workplace at all. Work-area screens are
 * therefore for HR and managers, even where the page would render an employee-scoped view.
 */
const WORK = APPROVERS

export const NAV: NavSection[] = [
  { title: 'Overview', items: [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, keywords: 'home' },
    { to: '/approvals', label: 'Approvals', icon: ListChecks, roles: APPROVERS, keywords: 'inbox pending approve reject requests queue' },
    { to: '/notifications', label: 'Notifications', icon: Bell, keywords: 'alerts updates inbox' },
    { to: '/feed', label: 'Feed', icon: Newspaper, keywords: 'social wall posts' },
  ]},
  { title: 'People', items: [
    { to: '/employees', label: 'Employees', icon: Users, roles: APPROVERS, keywords: 'staff directory people' },
    { to: '/onboarding', label: 'Onboarding', icon: UserPlus, roles: APPROVERS, keywords: 'joining new hire' },
    { to: '/offboarding', label: 'Offboarding', icon: UserMinus, roles: WORK, keywords: 'exit resignation fnf' },
    { to: '/org-chart', label: 'Org chart', icon: Network, roles: APPROVERS, keywords: 'hierarchy reporting' },
    { to: '/id-cards', label: 'ID cards', icon: IdCard, roles: HR, keywords: 'identity badge print qr reissue surrender' },
    { to: '/profile-approvals', label: 'Profile approvals', icon: UserCheck, roles: HR, keywords: 'bank change pan name nomination photo approve' },
    { to: '/pre-joiners', label: 'Pre-joiners', icon: UserCheck, roles: HR, keywords: 'selected candidate temp id kyc documents uan umang pre-onboarding offer accepted' },
    { to: '/recruitment', label: 'Recruitment', icon: Briefcase, roles: APPROVERS, keywords: 'hiring candidates jobs ats' },
  ]},
  { title: 'Time', items: [
    { to: '/attendance', label: 'Attendance', icon: CalendarCheck, roles: WORK, keywords: 'check in punch' },
    { to: '/attendance/regularize', label: 'Regularisation', icon: CalendarSync, roles: APPROVERS, keywords: 'regularize regularization missed punch correction' },
    { to: '/attendance/face-reviews', label: 'Face punch reviews', icon: ScanFace, roles: HR, keywords: 'face biometric photo liveness camera punch review' },
    { to: '/leave', label: 'Leave', icon: CalendarOff, roles: APPROVERS, keywords: 'time off vacation' },
    { to: '/shifts', label: 'Shifts', icon: CalendarClock, roles: WORK, keywords: 'roster schedule' },
    { to: '/holidays', label: 'Holidays', icon: CalendarHeart, roles: WORK },
    { to: '/overtime', label: 'Overtime', icon: Timer, roles: WORK, keywords: 'ot' },
  ]},
  { title: 'Payroll', items: [
    { to: '/payroll', label: 'Payroll runs', icon: Banknote, roles: HR, keywords: 'salary pay run payslip' },
    { to: '/salary-structure', label: 'Salary structures', icon: BadgeIndianRupee, roles: HR, keywords: 'ctc compensation' },
    { to: '/tax-declarations', label: 'Tax declarations', icon: ReceiptIndianRupee, roles: HR, keywords: 'tds 80c investment' },
    { to: '/reimbursements', label: 'Reimbursements', icon: HandCoins, roles: APPROVERS, keywords: 'claims expenses' },
    { to: '/loans', label: 'Loans & advances', icon: Landmark, roles: HR, keywords: 'emi advance' },
  ]},
  { title: 'Compliance', items: [
    { to: '/compliance', label: 'Compliance', icon: ShieldCheck, roles: [...HR, 'CA_PARTNER'], keywords: 'pf esi pt statutory' },
    { to: '/filings', label: 'Filings', icon: FileCheck2, roles: [...HR, 'CA_PARTNER'], keywords: 'ecr returns 24q form 16' },
    // ECR 2.0: EPFO electronic challan-cum-return generator, member master and establishment settings
    { to: '/compliance/ecr', label: 'ECR returns', icon: FileSpreadsheet, roles: [...HR, 'CA_PARTNER'], keywords: 'epf pf epfo ecr 2.0 challan return uan txt rfe validate approve export' },
    { to: '/compliance/ecr/members', label: 'ECR member master', icon: Contact, roles: [...HR, 'CA_PARTNER'], keywords: 'uan repository name mismatch return statement pdf pension pohw deferred international aadhaar' },
    { to: '/compliance/ecr/settings', label: 'ECR settings', icon: SlidersHorizontal, roles: [...HR, 'CA_PARTNER'], keywords: 'epf establishment code lin rate ceiling pro-rata admin charges edli' },
    // E-COMP: compliance calendar + payroll-operations productivity (Addendum E7)
    { to: '/compliance/calendar', label: 'Compliance calendar', icon: CalendarRange, roles: [...HR, 'CA_PARTNER'], keywords: 'due dates pf esi pt lwf tds 24q form 16 bonus overdue challan month-end close reminders' },
    { to: '/compliance/variance', label: 'Payroll variance', icon: GitCompareArrows, roles: HR, keywords: 'month on month compare joiners leavers changes before approval' },
    { to: '/compliance/data-gaps', label: 'Master-data gaps', icon: DatabaseZap, roles: [...HR, 'CA_PARTNER'], keywords: 'missing pan uan ifsc bank esi dob fix sheet' },
    { to: '/compliance/portfolio', label: 'Client portfolio', icon: BriefcaseBusiness, roles: [...HR, 'CA_PARTNER'], keywords: 'ca clients multi client access grant' },
    { to: '/audit-trail', label: 'Audit trail', icon: History, roles: ADMIN_ONLY, keywords: 'log history changes' },
  ]},
  { title: 'Workplace', items: [
    { to: '/performance', label: 'Performance', icon: Target, roles: WORK, keywords: 'goals reviews okr appraisal' },
    { to: '/announcements', label: 'Announcements', icon: Megaphone, roles: STAFF, keywords: 'notices' },
    { to: '/documents', label: 'Documents', icon: FolderOpen, roles: WORK, keywords: 'files' },
    { to: '/letters', label: 'Letters', icon: Mail, roles: HR, keywords: 'offer relieving experience' },
    { to: '/policies', label: 'Policies', icon: BookOpen, roles: STAFF, keywords: 'handbook' },
    { to: '/assets', label: 'Assets', icon: Laptop, roles: WORK, keywords: 'laptop equipment' },
    { to: '/helpdesk', label: 'Helpdesk', icon: LifeBuoy, roles: STAFF, keywords: 'tickets support' },
    { to: '/messaging', label: 'Messaging', icon: MessagesSquare, roles: HR, keywords: 'whatsapp sms' },
  ]},
  { title: 'Insights', items: [
    { to: '/reports', label: 'Reports', icon: ChartColumn, roles: [...HR, 'CA_PARTNER'], keywords: 'export register' },
    { to: '/analytics', label: 'Analytics', icon: ChartLine, roles: HR, keywords: 'insights trends' },
    // Spec 42 §3.10: upload/map/preview is HR_EXECUTIVE+ (rollback HR_ADMIN+, enforced in UI + API).
    { to: '/import', label: 'Import data', icon: FileUp, roles: HR, keywords: 'excel csv upload bulk' },
  ]},
  { title: 'My space', items: [
    { to: '/ess/profile', label: 'My profile', icon: CircleUser, keywords: 'me account' },
    // E-ESS (Addendum E4): a manager's own team; CA partners have no employee record, so the
    // rest of My space is for staff only (the profile page still carries their account security).
    { to: '/ess/team', label: 'My team', icon: UsersRound, roles: APPROVERS, keywords: 'team reports direct reports who is in leave calendar mss' },
    { to: '/ess/requests', label: 'My requests', icon: ClipboardList, roles: STAFF, keywords: 'track status pending approved where is my claim leave tracker' },
    { to: '/ess/compensation', label: 'My compensation', icon: Wallet, keywords: 'salary structure ctc ctc breakup take home net pay gross monthly annual package', roles: STAFF },
    { to: '/ess/payslips', label: 'My payslips', icon: ReceiptText, keywords: 'salary slip', roles: STAFF },
    { to: '/ess/leaves', label: 'My leave', icon: CalendarOff, roles: STAFF },
    { to: '/ess/attendance', label: 'My attendance', icon: CalendarCheck, roles: STAFF },
    { to: '/ess/face-attendance', label: 'Face attendance', icon: ScanFace, keywords: 'face camera clock in biometric consent', roles: STAFF },
    { to: '/ess/tax', label: 'My tax', icon: ReceiptIndianRupee, roles: STAFF },
    { to: '/ess/reimbursements', label: 'My claims', icon: HandCoins, roles: STAFF },
    { to: '/ess/loans', label: 'My loans', icon: Landmark, roles: STAFF },
    { to: '/ess/documents', label: 'My documents', icon: FolderOpen, roles: STAFF },
    { to: '/ess/assets', label: 'My assets', icon: Laptop, roles: STAFF },
    { to: '/ess/letters', label: 'My letters', icon: Mail, roles: STAFF },
    // Self-service views of shared screens, shown in My space to EMPLOYEE only (their
    // work-area entries above are hidden for that role).
    { to: '/performance', label: 'My goals & reviews', icon: Target, audience: ['EMPLOYEE'], keywords: 'goals self review appraisal okr' },
    { to: '/holidays', label: 'Holiday calendar', icon: CalendarHeart, audience: ['EMPLOYEE'], keywords: 'holidays public festival' },
    { to: '/ess/onboarding', label: 'My onboarding', icon: ClipboardCheck, roles: STAFF, keywords: 'joining checklist new joiner induction tasks' },
    { to: '/ess/resignation', label: 'Resignation', icon: DoorOpen, roles: STAFF, keywords: 'resign exit notice withdraw leave the company' },
  ]},
  { title: 'Settings', items: [
    { to: '/settings/org', label: 'Organisation', icon: Building2, roles: ADMIN_ONLY },
    { to: '/settings/departments', label: 'Departments', icon: Layers, roles: ADMIN_ONLY },
    { to: '/settings/positions', label: 'Positions', icon: GitBranch, roles: HR, keywords: 'position job seat hierarchy reports to team lead manager grade band headcount vacancy' },
    { to: '/settings/assistant', label: 'Assistant', icon: Sparkles, roles: ADMIN_ONLY, keywords: 'assistant ai help claude feedback retention' },
    { to: '/settings/branding', label: 'Branding', icon: Palette, roles: ADMIN_ONLY },
    { to: '/settings/modules', label: 'Modules', icon: LayoutGrid, roles: ADMIN_ONLY },
    { to: '/settings/face-attendance', label: 'Face attendance', icon: ScanFace, roles: ADMIN_ONLY, keywords: 'face biometric photo retention geofence liveness' },
    { to: '/settings/gl-mapping', label: 'Ledger mapping', icon: BookOpenCheck, roles: ADMIN_ONLY, keywords: 'gl journal accounting tally ledger cost centre payroll' },
    { to: '/settings/backup', label: 'Backup', icon: DatabaseBackup, roles: ADMIN_ONLY },
  ]},
  { title: 'Platform', items: [
    { to: '/admin', label: 'Overview', icon: Gauge, roles: PLATFORM },
    { to: '/admin/organizations', label: 'Organisations', icon: Building, roles: PLATFORM },
    { to: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard, roles: PLATFORM },
    { to: '/admin/plans', label: 'Plans', icon: Layers, roles: PLATFORM },
    { to: '/admin/billing', label: 'Billing', icon: BadgeIndianRupee, roles: PLATFORM },
    { to: '/admin/api-keys', label: 'API keys', icon: KeyRound, roles: PLATFORM },
    { to: '/admin/access', label: 'Access', icon: UserCog, roles: PLATFORM },
    { to: '/admin/theme', label: 'Theme', icon: Palette, roles: PLATFORM },
    { to: '/admin/analytics', label: 'Platform analytics', icon: ChartPie, roles: PLATFORM },
    { to: '/admin/cms', label: 'Website CMS', icon: PanelsTopLeft, roles: [...PLATFORM, ...ADMIN_ONLY] },
  ]},
]

/** Unused-import guard: HR is exported for pages that want the same role set. */
export const HR_ROLES = HR

/**
 * SPA path → the switchable module that owns it (mirrors App\Support\Modules\ModuleRegistry on
 * the API). A nav item whose module is switched off for the organisation is hidden; the API
 * blocks the module regardless (403 with a plain message).
 */
const MODULE_BY_PREFIX: Record<string, string> = {
  onboarding: 'onboarding', offboarding: 'offboarding', recruitment: 'recruitment',
  attendance: 'attendance', leave: 'leave', shifts: 'shifts', holidays: 'holidays', overtime: 'overtime',
  payroll: 'payroll', 'salary-structure': 'salary_structure', 'tax-declarations': 'tax',
  reimbursements: 'reimbursements', loans: 'loans', compliance: 'compliance', filings: 'filings',
  performance: 'performance', announcements: 'announcements', documents: 'documents', letters: 'letters',
  assets: 'assets', helpdesk: 'helpdesk', messaging: 'messaging', reports: 'reports', analytics: 'analytics',
  governance: 'governance',
}
const ESS_MODULE: Record<string, string> = {
  payslips: 'payroll', leaves: 'leave', attendance: 'attendance', 'face-attendance': 'attendance', tax: 'tax',
  reimbursements: 'reimbursements', loans: 'loans', documents: 'documents', assets: 'assets', policies: 'documents',
  onboarding: 'onboarding', resignation: 'offboarding',
}
export function moduleForPath(path: string): string | null {
  const [first, second] = path.replace(/^\//, '').split('/')
  if (first === 'ess') return ESS_MODULE[second ?? ''] ?? null
  return MODULE_BY_PREFIX[first] ?? null
}
